import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { startWith } from 'rxjs';
import { TaskService } from '../../services/task.service';
import { FixedEntityField, isFixedEntitySchemaRef } from '../../models/task-config.model';
import { FileTransferComponent } from '../file-transfer/file-transfer.component';
import { ToastService } from '../../services/toast.service';
import { computeFixedFieldTotals } from '../../utils/sum-fixed-field-lengths';
import { ViewSchemaEditorComponent } from '../view-schema-editor/view-schema-editor.component';

@Component({
  selector: 'app-fixed-task-config',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FileTransferComponent, ViewSchemaEditorComponent],
  templateUrl: './fixed-task-config.component.html',
  styleUrl: './fixed-task-config.component.scss',
})
export class FixedTaskConfigComponent implements OnInit {
  private fb = inject(FormBuilder);
  private taskService = inject(TaskService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private fieldIdCounter = 0;

  isExecuting = signal(false);
  isSaving = signal(false);
  isLoading = signal(false);
  isLoadingResult = signal(false);
  hasExecuted = signal(false);
  error = signal<string | null>(null);
  /** 変換結果 JSON（パースせずテキストとして保持） */
  resultRawText = signal<string>('');
  /** 既定はプレビュー表示（全文はユーザー操作で表示） */
  showFullResult = signal(false);
  /** 全文表示を警告する閾値（文字数） */
  private readonly FULL_RENDER_WARN_LIMIT = 300_000;
  /** これ以上は全文表示を禁止（ブラウザフリーズ回避のため） */
  private readonly FULL_RENDER_HARD_LIMIT = 2_000_000;
  /** プレビューで表示する最大文字数 */
  readonly previewLimit = 80_000;

  /** 結果表示用（プレビュー/全文の切替を反映） */
  resultShownText = computed(() => {
    const s = this.resultRawText();
    if (this.showFullResult()) return s;
    if (s.length <= this.previewLimit) return s;
    return `${s.slice(0, this.previewLimit)}\n...（省略: 全 ${s.length.toLocaleString()} 文字）`;
  });

  /** 全文表示が重くなりやすいサイズか */
  isLargeResult = computed(() => this.resultRawText().length >= this.FULL_RENDER_WARN_LIMIT);
  /** 各フィールド length の合計（レイアウト確認用） */
  totalFieldLength = signal(0);
  /** 各フィールド count（1 以上）の合計 */
  totalFieldCountSum = signal(0);

  private readonly BUCKET_CODE = 'x93jkd8f-qpz1-mv8w';

  configForm: FormGroup = this.fb.group({
    wbsTaskId: ['demo-fixtext-aden-to-json', Validators.required],
    id: ['demo-fixtext-aden-to-json', Validators.required],
    name: ['清野テスト固定長JSON変換タスク', Validators.required],
    searchPath: ['seino/input/delivery_data.txt', Validators.required],
    savePath: ['seino/output/delivery_data.json', Validators.required],
    /** インライン: フォームの fields / 外部: 別ファイルの schemaPath */
    schemaMode: this.fb.control<'inline' | 'external'>('inline', { nonNullable: true }),
    schemaPath: [''],
    entityFields: this.fb.array([]),
    viewSchemaJson: [''],
    crossJoinOptionsJson: [''],
    saveModelJson: [''],
  });

  ngOnInit() {
    this.entityFields.valueChanges
      .pipe(startWith(this.entityFields.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const t = computeFixedFieldTotals(this.entityFields.getRawValue());
        this.totalFieldLength.set(t.lengthSum);
        this.totalFieldCountSum.set(t.countSum);
      });
    this.configForm
      .get('schemaMode')
      ?.valueChanges.pipe(startWith(this.configForm.get('schemaMode')?.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((mode) => this.applySchemaMode(mode as 'inline' | 'external'));
    this.applySchemaMode(this.configForm.get('schemaMode')?.value as 'inline' | 'external');
    this.fetchSavedConfig();
  }

  /** スキーマモードに応じたバリデーションと entityFields の活性 */
  private applySchemaMode(mode: 'inline' | 'external'): void {
    const schemaPathCtrl = this.configForm.get('schemaPath');
    if (mode === 'external') {
      schemaPathCtrl?.setValidators([Validators.required]);
      this.entityFields.disable({ emitEvent: false });
    } else {
      schemaPathCtrl?.clearValidators();
      this.entityFields.enable({ emitEvent: false });
    }
    schemaPathCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  get entityFields(): FormArray {
    return this.configForm.get('entityFields') as FormArray;
  }

  addField(field?: Partial<FixedEntityField>): void {
    const fieldGroup = this.fb.group({
      // 並び替え時に index track だとDOMが再利用されて表示が動かないため、安定IDを付与する
      _id: [(field as { _id?: number } | undefined)?._id ?? this.fieldIdCounter++],
      fieldName: [field?.fieldName || '', Validators.required],
      length: [field?.length || null, [Validators.required, Validators.min(1)]],
      trimType: [field?.trimType || ''],
      fieldType: [field?.fieldType || ''],
      // 未指定時は API に count を送らない（任意・保存時に 1 以上のみ付与）
      count: [field?.count != null ? field.count : null],
      format: [field?.format ?? ''],
      textAlignment: [field?.textAlignment ?? ''],
    });
    this.entityFields.push(fieldGroup);
  }

  removeField(index: number): void {
    this.entityFields.removeAt(index);
  }

  moveFieldUp(index: number): void {
    if (index <= 0) return;
    const field = this.entityFields.at(index);
    this.entityFields.removeAt(index);
    this.entityFields.insert(index - 1, field);
  }

  moveFieldDown(index: number): void {
    if (index >= this.entityFields.length - 1) return;
    const field = this.entityFields.at(index);
    this.entityFields.removeAt(index);
    this.entityFields.insert(index + 1, field);
  }

  fetchSavedConfig(): void {
    const taskId = this.configForm.get('wbsTaskId')?.value;
    if (!taskId) return;

    this.isLoading.set(true);
    this.taskService.getTaskFile(taskId).subscribe({
      next: (res) => {
        const config = (res as any)?.data;
        const converter = config?.profiles?.actions?.converter;

        if (config && converter) {
          const ent = converter.entitySchema;
          const isRef = ent && isFixedEntitySchemaRef(ent);

          this.configForm.patchValue({
            wbsTaskId: taskId,
            id: config.id,
            name: config.name,
            searchPath: converter.target?.searchPath || '',
            savePath: converter.saveTarget?.savePath || '',
            schemaMode: isRef ? 'external' : 'inline',
            schemaPath: isRef ? (ent as { schemaPath: string }).schemaPath : '',
            viewSchemaJson:
              converter.viewSchema && Object.keys(converter.viewSchema).length > 0
                ? JSON.stringify(converter.viewSchema, null, 2)
                : '',
            crossJoinOptionsJson:
              converter.crossJoinOptions && Object.keys(converter.crossJoinOptions).length > 0
                ? JSON.stringify(converter.crossJoinOptions, null, 2)
                : '',
            saveModelJson:
              converter.saveTarget?.saveModel &&
              Object.keys(converter.saveTarget.saveModel).length > 0
                ? JSON.stringify(converter.saveTarget.saveModel, null, 2)
                : '',
          });
          this.applySchemaMode(isRef ? 'external' : 'inline');

          this.entityFields.clear();
          if (isRef) {
            // 外部スキーマ時はフォーム上の行定義は使わない
          } else {
            const savedFields = ent?.fields || [];
            savedFields.forEach((field: FixedEntityField) => {
              this.addField(field);
            });
          }
        }
        this.isLoading.set(false);
      },
      error: () => {
        // Initialization if no config found or error
        if (this.entityFields.length === 0) {
          this.addField({ fieldName: '商品名', length: 20, trimType: 'rtrim' });
          this.addField({ fieldName: '部門名', length: 10, trimType: 'rtrim' });
        }
        this.isLoading.set(false);
      },
    });
  }

  saveConfig(): void {
    const mode = this.configForm.get('schemaMode')?.value;
    if (mode === 'inline' && this.entityFields.length === 0) {
      this.error.set('インライン定義では1件以上のフィールドが必要です。');
      this.configForm.markAllAsTouched();
      return;
    }
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.error.set(null);

    // 空の任意項目はペイロードから除外
    type EntityFieldRow = {
      fieldName: string;
      length: number | string | null;
      trimType?: string;
      fieldType?: string;
      count?: number | string | null;
      format?: string;
      textAlignment?: string;
    };

    const formValue = this.configForm.getRawValue() as {
      wbsTaskId: string;
      id: string;
      name: string;
      searchPath: string;
      savePath: string;
      schemaMode: 'inline' | 'external';
      schemaPath: string;
      entityFields: EntityFieldRow[];
      viewSchemaJson: string;
      crossJoinOptionsJson: string;
      saveModelJson: string;
    };

    let viewSchema = null;
    let crossJoinOptions = null;
    let saveModel = null;

    try {
      if (formValue.viewSchemaJson && formValue.viewSchemaJson.trim() !== '') {
        viewSchema = JSON.parse(formValue.viewSchemaJson);
      }
      if (formValue.crossJoinOptionsJson && formValue.crossJoinOptionsJson.trim() !== '') {
        crossJoinOptions = JSON.parse(formValue.crossJoinOptionsJson);
      }
      if (formValue.saveModelJson && formValue.saveModelJson.trim() !== '') {
        saveModel = JSON.parse(formValue.saveModelJson);
      }
    } catch (e) {
      this.error.set(
        'JSONの形式が不正です。View Schema, Cross Join Options, Save ModelのJSONを確認してください。',
      );
      this.isSaving.set(false);
      return;
    }

    const fields = formValue.entityFields.map((f: EntityFieldRow) => {
      const field: Record<string, unknown> = { fieldName: f.fieldName, length: Number(f.length) };
      if (f.trimType) field['trimType'] = f.trimType;
      if (f.fieldType) field['fieldType'] = f.fieldType;
      const countRaw = f.count;
      const countNum = countRaw != null && countRaw !== '' ? Number(countRaw) : NaN;
      if (Number.isFinite(countNum) && countNum >= 1) {
        field['count'] = countNum;
      }
      const fmt = typeof f.format === 'string' ? f.format.trim() : '';
      if (fmt) field['format'] = fmt;
      const align = typeof f.textAlignment === 'string' ? f.textAlignment.trim() : '';
      if (align) field['textAlignment'] = align;
      return field;
    });

    const entitySchema =
      formValue.schemaMode === 'external'
        ? { schemaPath: formValue.schemaPath.trim() }
        : {
            contentsType: 'fixed' as const,
            charset: 'shift_jis',
            fields,
          };

    const payload: any = {
      id: formValue.id,
      name: formValue.name,
      wbsType: 'task',
      profiles: {
        actions: {
          actionType: 'execute',
          converter: {
            target: {
              searchPath: formValue.searchPath,
            },
            entitySchema,
            saveTarget: {
              savePath: formValue.savePath,
            },
          },
        },
      },
    };

    if (viewSchema) payload.profiles.actions.converter.viewSchema = viewSchema;
    if (crossJoinOptions) payload.profiles.actions.converter.crossJoinOptions = crossJoinOptions;
    if (saveModel) payload.profiles.actions.converter.saveTarget.saveModel = saveModel;

    const wbsTaskId = formValue.wbsTaskId || formValue.id;

    // Use taskService to post config. The service has postTaskFile which accepts TaskConfig.
    this.taskService.postTaskFile(payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.toastService.success('設定を保存しました');
      },
      error: (err) => {
        this.isSaving.set(false);
        this.error.set('設定の保存に失敗しました');
        console.error('Save config error:', err);
      },
    });
  }

  executeTask(): void {
    this.isExecuting.set(true);
    this.error.set(null);
    this.hasExecuted.set(true);
    this.isLoadingResult.set(true);
    this.resultRawText.set('');
    this.showFullResult.set(false);

    const formValue = this.configForm.getRawValue();
    const wbsTaskId = formValue.wbsTaskId || formValue.id;

    this.taskService.runConvertTask(wbsTaskId, this.BUCKET_CODE).subscribe({
      next: (response) => {
        if (response.resultCode === 0) {
          // If successful, read the output file
          const savePath = formValue.savePath;
          this.fetchFileData(savePath);
        } else {
          this.error.set(response.resultMessage || 'タスクの実行に失敗しました。');
          this.isExecuting.set(false);
          this.isLoadingResult.set(false);
        }
      },
      error: (err) => {
        this.error.set('タスクの実行に失敗しました。接続を確認してください。');
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
        console.error('Task execution error:', err);
      },
    });
  }

  private fetchFileData(savePath: string): void {
    // 大きい JSON は object 化→再 stringify で固まりやすいので、テキストとして取得する
    this.taskService.getFileText(savePath).subscribe({
      next: (text) => {
        this.resultRawText.set(text ?? '');
        this.showFullResult.set(false);
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
      },
      error: (err) => {
        this.error.set(`変換結果ファイル (${savePath}) の取得に失敗しました。`);
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
        console.error('File fetch error:', err);
      },
    });
  }

  toggleFullResult(): void {
    const next = !this.showFullResult();
    if (!next) {
      this.showFullResult.set(false);
      return;
    }
    const len = this.resultRawText().length;
    if (len >= this.FULL_RENDER_HARD_LIMIT) {
      this.toastService.error(
        `結果が大きすぎるため全文表示はできません（${len.toLocaleString()} 文字）。ダウンロードをご利用ください。`,
      );
      this.showFullResult.set(false);
      return;
    }
    this.showFullResult.set(true);
  }

  downloadResult(): void {
    const savePath = String(this.configForm.get('savePath')?.value ?? '').trim();
    if (!savePath) {
      this.toastService.error('出力ファイルパスが未設定です');
      return;
    }

    this.taskService.downloadFile(savePath).subscribe({
      next: ({ blob, suggestedFileName }) => {
        const url = URL.createObjectURL(blob);
        try {
          const a = document.createElement('a');
          a.href = url;
          a.download = suggestedFileName || 'result.json';
          a.click();
        } finally {
          // 直後に revoke してもDLが失敗するブラウザがあるため、少し遅延して解放する
          setTimeout(() => URL.revokeObjectURL(url), 5_000);
        }
      },
      error: (err) => {
        console.error('downloadResult', err);
        this.toastService.error('ダウンロードに失敗しました');
      },
    });
  }
}
